import { Skeleton, Stack } from "@mui/material";
import title from "global/constants/TitleConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { CampaignCount, CampaignOverviewType } from "models/interfaces";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTitle } from "utils/UseTitle";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import {
  fetchFailedResponse,
  fetchFailedResponseNew,
  getWholeCampaignDetails,
} from "../CampaignService";
import ViewABCampaign from "./ViewABCampaign";
import ViewRegularCampaign from "./ViewRegularCampaign";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";

const ViewCampaign = (props: any) => {
  useTitle(title.STATS);
  const urlParams = new URLSearchParams(useLocation().search);
  const id = urlParams.get("id");
  const [isLoading, setIsLoading] = useState(true);
  const [campaignDetails, setCampaignDetails] =
    useState<CampaignOverviewType>();
  const [failedMetrics, setFailedMetrics] = useState<CampaignCount[]>([]);
  const [failedMetricsNew, setFailedMetricsNew] = useState<number>(0);
  const parentCampaignType = props.location?.state?.parentCampaignType;

  useEffect(() => {
    if (isTruthy(id)) {
      fetchCampaignDetails();
    }
  }, [id]);

  const fetchCampaignDetails = async () => {
    try {
      setIsLoading(true);
      const [campaign, failedMetrics, failedMetricsNew] = await Promise.all([
        getWholeCampaignDetails(Math.abs(parseInt(id!))),
        fetchFailedResponse(Math.abs(parseInt(id!))),
        fetchFailedResponseNew(Math.abs(parseInt(id!))),
      ]);
      if (campaign.type === "Regular") {
        if (
          campaign.campaign.status === "Draft" ||
          campaign.campaign.status === "Submitted"
        )
          return history.push(viewpaths.campaignsViewPath);
      }
      setCampaignDetails(campaign);
      setFailedMetrics(failedMetrics);
      setFailedMetricsNew(failedMetricsNew);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getSkeleton = () => {
    return (
      <CenteredAuthenticatedLayout>
        <Stack spacing={2}>
          <Skeleton variant="text" width={120} height={30} />
          {/* Header Skeleton */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="column" spacing={1}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Skeleton variant="text" width={300} height={60} />
                <Skeleton variant="text" width={90} height={60} />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Skeleton variant="text" width={120} height={30} />
                <Skeleton variant="text" width={200} height={30} />
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Skeleton variant="rounded" width={100} height={40} />
              {parentCampaignType !== "ABTesting" && (
                <Skeleton variant="rounded" width={100} height={40} />
              )}
              {parentCampaignType !== "ABTesting" && (
                <Skeleton variant="rounded" width={100} height={40} />
              )}
            </Stack>
          </Stack>

          {/* Tabs Skeleton */}
          <Stack direction="row" spacing={2}>
            <Skeleton variant="rounded" width={100} height={30} />
            <Skeleton variant="rounded" width={100} height={30} />
            <Skeleton variant="rounded" width={100} height={30} />
          </Stack>

          {/* Campaign Stats Skeleton */}
          <Skeleton variant="rounded" width="100%" height={150} />

          {/* Performance Report Skeleton */}
          <Skeleton variant="rounded" width="100%" height={200} />

          {/* Link Activity Skeleton */}
          <Skeleton variant="rounded" width="100%" height={200} />
        </Stack>
      </CenteredAuthenticatedLayout>
    );
  };

  const getBody = () => {
    switch (campaignDetails?.campaign.type) {
      case "Regular": {
        return (
          <ViewRegularCampaign
            campaignData={campaignDetails}
            setCampaignData={setCampaignDetails}
            failedMetrics={failedMetrics}
            failedMetricsNew={failedMetricsNew}
            parentCampaignType={parentCampaignType}
          />
        );
      }
      case "ABTesting": {
        return (
          <ViewABCampaign
            campaignData={campaignDetails}
            setCampaignData={setCampaignDetails}
            failedMetrics={failedMetrics}
            refresh={() => fetchCampaignDetails()}
          />
        );
      }
      default: {
        history.push(viewpaths.campaignsViewPath);
        return null;
      }
    }
  };

  if (isLoading) {
    return getSkeleton();
  }
  return getBody();
};

export default ViewCampaign;
